import { MouseEvent, ReactNode, useEffect, useMemo, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Code2, ExternalLink, XCircle } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { cn } from '@/frontend/lib/utils';
import { OutlineStore } from '@/frontend/reports/primitives';
import { evaluateReportRuntime } from '@/frontend/reports/loader';
import { useSchemaStore } from '@/frontend/store/SchemaStore';
import { parseSchemaRef, schemaRefSource } from '@/reports/bundles/inputSheet';
import { CompiledReportBundle } from '@/reports/bundles/types';
import { FieldMeta } from '@/reports/schema';
import { BundleSchemaInfo } from '@/types/reportBundleTypes';
import { Button } from '../../ui/button';

/**
 * The Visual view of the files whose visual form is read rather than drawn:
 * what a definition declares, a document's outline as it prints, and which
 * schema `schema.json` names. Each says where it comes from, and the code is
 * a switch away.
 */

function Card({ title, aside, children }: { title: string; aside?: ReactNode; children: ReactNode }) {
  return (
    <section aria-label={title} className="flex flex-col gap-2 rounded-xl border-[1px] bg-background p-[1rem]">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-[0.7rem] font-semibold tracking-wide text-muted-foreground uppercase">{title}</h3>
        {aside}
      </div>
      {children}
    </section>
  );
}

function Row({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="flex min-h-7 items-baseline gap-3 border-b-[1px] border-border/60 py-1 text-sm last:border-b-0">
      <span className="w-32 shrink-0 text-muted-foreground @xl:w-40">{label}</span>
      <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{children}</span>
    </div>
  );
}

const fieldsOf = (meta: Record<string, FieldMeta> | undefined) => Object.entries(meta ?? {}).filter(([, m]) => !m.hidden);

function describeField(meta: FieldMeta): string {
  return [meta.kind, meta.unit, meta.optional ? I18n.get('designerOptional') : null].filter(Boolean).join(' · ');
}

/**
 * definition.ts, shown as what it declares: the report's title and version,
 * what it reads, what it asks on the report itself, its settings and sign-off
 * lines, and the findings its judgement came to on each fixture. Read from
 * what the draft last compiled to; it changes in the code.
 */
export function DefinitionView({ compiled, onOpenCode }: { compiled: CompiledReportBundle | null; onOpenCode: () => void }) {
  const definition = useMemo(() => {
    if (!compiled) return null;
    try {
      const module = evaluateReportRuntime({ bundleVersionId: `draft:${compiled.hash}`, key: compiled.key, contractVersion: compiled.contractVersion, definitionJs: compiled.definitionJs, documentJs: compiled.documentJs, hash: compiled.hash });
      return module.definition as unknown as { valuesSchema?: { meta: Record<string, FieldMeta> }; settingsSchema?: { meta: Record<string, FieldMeta> }; defaultSettings?: () => Record<string, unknown> };
    } catch {
      return null;
    }
  }, [compiled?.hash]);

  if (!compiled) {
    return (
      <Shell onOpenCode={onOpenCode} note={I18n.get('designerDefinitionNotCompiled')}>
        <p className="text-sm text-muted-foreground">{I18n.get('reportBundleNotCompiled')}</p>
      </Shell>
    );
  }
  const asked = fieldsOf(definition?.valuesSchema?.meta);
  const settings = fieldsOf(definition?.settingsSchema?.meta);
  const defaults = definition?.defaultSettings?.() ?? {};
  const signoffs = compiled.meta?.signoffs ?? [];
  const requires = Object.keys(compiled.meta?.requires ?? {});
  return (
    <Shell onOpenCode={onOpenCode} note={I18n.get('designerDefinitionNote')}>
      <div className="grid gap-[1rem] @4xl:grid-cols-2">
        <Card title={I18n.get('designerDefinitionReport')}>
          <Row label={I18n.get('reportBundleTitle')}>{compiled.title}</Row>
          {compiled.description && <Row label={I18n.get('reportBundleDescription')}>{compiled.description}</Row>}
          <Row label={I18n.get('version')}>{compiled.definitionVersion}</Row>
        </Card>
        <Card title={I18n.get('designerDefinitionReads')}>
          <Row label={I18n.get('designerDefinitionSubject')}>{I18n.get(compiled.subject === 'inputs' ? 'reportBundleReportsOnInputs' : 'reportBundleReportsOnRun')}</Row>
          {compiled.schema && <Row label={I18n.get('schema')}>{compiled.schema.key} · {compiled.schema.version === null ? I18n.get('designerFollowsDraft') : `v${compiled.schema.version}`}</Row>}
          {requires.length > 0 && <Row label={I18n.get('designerDefinitionRequires')}><span className="font-mono text-xs">{requires.join(', ')}</span></Row>}
        </Card>
        <Card title={I18n.get('designerDefinitionAsked')}>
          {asked.length === 0 ? <p className="text-sm text-muted-foreground">{I18n.get('designerNothing')}</p> : asked.map(([key, meta]) => <Row key={key} label={meta.label ?? key}>{describeField(meta)}</Row>)}
        </Card>
        <Card title={I18n.get('designerDefinitionSignoffs')}>
          {signoffs.length === 0 ? <p className="text-sm text-muted-foreground">{I18n.get('designerNothing')}</p> : signoffs.map((s) => <Row key={s.id} label={s.label}><span className="font-mono text-xs text-muted-foreground">{s.id}</span> · {s.optional ? I18n.get('designerOptional') : I18n.get('designerRequired')}</Row>)}
        </Card>
        {settings.length > 0 && (
          <Card title={I18n.get('designerDefinitionSettings')}>
            {settings.map(([key, meta]) => <Row key={key} label={meta.label ?? key}>{String(defaults[key] ?? '—')}{meta.unit ? ` ${meta.unit}` : ''} <span className="text-muted-foreground">· {I18n.get('designerDefault')}</span></Row>)}
          </Card>
        )}
        <Card title={I18n.get('designerDefinitionJudgement')}>
          {(compiled.fixtureResults ?? []).length === 0 ? <p className="text-sm text-muted-foreground">{I18n.get('reportBundleNoFixtures')}</p> : (compiled.fixtureResults ?? []).map((r) => (
            <Row key={r.file} label={<span className="font-mono text-xs">{r.file.replace(/^fixtures\//, '')}</span>}>
              <span className={cn('inline-flex items-center gap-1.5', r.ok ? 'text-emerald-800' : 'text-red-700')}>
                {r.ok ? <CheckCircle2 className="size-3.5" /> : <XCircle className="size-3.5" />}
                {r.verdictLabel ?? '—'} · {I18n.get('designerFindings', { count: String(r.findings) })}
              </span>
              {!!r.reasons?.length && <span className="block text-xs text-muted-foreground">{r.reasons.join(' · ')}</span>}
            </Row>
          ))}
        </Card>
      </div>
    </Shell>
  );
}

function Shell({ note, onOpenCode, children }: { note: string; onOpenCode: () => void; children: ReactNode }) {
  return (
    <div className="@container h-full overflow-y-auto bg-muted/30">
      <div className="mx-auto flex max-w-[64rem] flex-col gap-[1rem] px-[1.5rem] py-[1.25rem]">
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>{note}</span>
          <Button variant="outline" size="sm" onClick={onOpenCode}><Code2 /> {I18n.get('rv2OpenInCode')}</Button>
        </div>
        {children}
      </div>
    </div>
  );
}

/** The line of the document's code a section's title is written on, to jump to; null when it is built some other way. */
export function lineOf(source: string, text: string): number | null {
  const lines = source.split('\n');
  const quoted = [`"${text}"`, `'${text}'`, `\`${text}\``];
  const index = lines.findIndex((line) => quoted.some((q) => line.includes(q)));
  return index < 0 ? null : index + 1;
}

/**
 * document.tsx, shown as what it prints, read from the preview: its sections
 * in order and the fields and sign-offs in each, each with the line of code
 * its title is written on where the code spells it out.
 */
export function DocumentOutlineView({ outline, source, previewing, onOpenCode, onReveal }: {
  outline: OutlineStore;
  source: string;
  /** what the preview reads, which is what the outline is of */
  previewing: string;
  onOpenCode: () => void;
  onReveal: (line: number) => void;
}) {
  const snapshot = useSyncExternalStore(outline.subscribe, outline.getSnapshot);
  return (
    <Shell onOpenCode={onOpenCode} note={I18n.get('designerOutlineNote', { previewing })}>
      {snapshot.sections.length === 0 ? (
        <p className="text-sm text-muted-foreground">{I18n.get('designerOutlineEmpty')}</p>
      ) : (
        <ol aria-label={I18n.get('designerOutline')} className="overflow-hidden rounded-xl border-[1px] bg-background">
          {snapshot.sections.map((section) => {
            const line = lineOf(source, section.title);
            const entries = snapshot.entries.filter((e) => e.section === section.key);
            return (
              <li key={section.key} className="border-b-[1px] last:border-b-0">
                <div className="flex items-baseline gap-3 px-[1rem] py-2">
                  <span className="font-medium">{section.number ? `${section.number} · ` : ''}{section.title}</span>
                  {line !== null && <button type="button" className="ml-auto font-mono text-xs text-primary hover:underline" onClick={() => onReveal(line)}>{I18n.get('designerLine', { line: String(line) })}</button>}
                </div>
                {entries.length > 0 && (
                  <ul className="flex flex-col gap-0.5 bg-muted/30 px-[1rem] py-1.5 pl-[2rem] text-xs">
                    {entries.map((e) => (
                      <li key={e.id} className="flex items-center gap-2">
                        <span>{e.label}</span>
                        <span className="text-muted-foreground">{I18n.get(e.kind === 'signoff' ? 'designerOutlineSignoff' : 'designerOutlineField')}{e.optional ? ` · ${I18n.get('designerOptional')}` : ''}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </Shell>
  );
}

/**
 * schema.json, shown as which schema the report reads, and whether it follows
 * that schema's draft — edited from here, and shared with every report that
 * follows it — or is fixed at one published version.
 */
export function SchemaRefView({ text, schema, readOnly, onChange, onOpenCode, onOpenSchema }: {
  text: string;
  schema: BundleSchemaInfo | null;
  readOnly?: boolean;
  onChange: (text: string) => void;
  onOpenCode: () => void;
  /** the link to the schema's own page followed: the designer saves what is unsaved before it leaves */
  onOpenSchema?: (e: MouseEvent<HTMLAnchorElement>) => void;
}) {
  const schemas = useSchemaStore((s) => s.schemas);
  const fetchSchemas = useSchemaStore((s) => s.fetchSchemas);
  useEffect(() => {
    if (schemas === null) void fetchSchemas();
  }, [schemas, fetchSchemas]);
  const { ref, problems } = parseSchemaRef(text);
  const current = schemas?.find((s) => s.key === ref?.schema) ?? null;
  const latest = current?.latestVersion?.number ?? 0;
  const versions = Array.from({ length: latest }, (_, i) => latest - i);
  const set = (key: string, version?: number) => onChange(schemaRefSource(version ? { schema: key, version } : { schema: key }));
  return (
    <Shell onOpenCode={onOpenCode} note={I18n.get('designerSchemaRefNote')}>
      {!ref ? (
        <div role="alert" className="rounded-lg border-[1px] border-destructive/40 bg-destructive/5 p-[0.75rem] text-sm text-destructive">{problems.join(' ')}</div>
      ) : (
        <Card title={I18n.get('designerSchemaRefReads')} aside={schema && <Link to={`/report-studio/schemas/${schema.id}`} onClick={onOpenSchema} className="inline-flex items-center gap-1 text-xs text-primary hover:underline"><ExternalLink className="size-3" /> {I18n.get('designerOpenSchemaAlone')}</Link>}>
          <label className="grid gap-1 text-sm">
            <span className="text-muted-foreground">{I18n.get('schema')}</span>
            <select aria-label={I18n.get('schema')} disabled={readOnly} value={ref.schema} onChange={(e) => set(e.target.value)} className="h-8 rounded-lg border-[1px] border-input bg-background px-2 text-sm">
              {!current && <option value={ref.schema}>{ref.schema}</option>}
              {(schemas ?? []).map((s) => <option key={s.id} value={s.key}>{s.title}</option>)}
            </select>
          </label>
          <label className="grid gap-1 text-sm">
            <span className="text-muted-foreground">{I18n.get('designerSchemaRefWhich')}</span>
            <select aria-label={I18n.get('designerSchemaRefWhich')} disabled={readOnly} value={ref.version ?? ''} onChange={(e) => set(ref.schema, e.target.value ? Number(e.target.value) : undefined)} className="h-8 rounded-lg border-[1px] border-input bg-background px-2 text-sm">
              <option value="">{I18n.get('reportBundleSchemaFollow')}</option>
              {versions.map((v) => <option key={v} value={v}>{I18n.get('reportBundleSchemaPin', { number: String(v) })}</option>)}
              {ref.version && ref.version > latest && <option value={ref.version}>v{ref.version}</option>}
            </select>
          </label>
          {schema && schema.usedBy.length > 1 && <p className="text-xs text-muted-foreground">{I18n.get('rv2SchemaShared', { reports: schema.usedBy.map((u) => u.title).join(', ') })}</p>}
          <p className="text-xs text-muted-foreground">{I18n.get('designerSchemaRefSaveNote')}</p>
        </Card>
      )}
    </Shell>
  );
}
